import { getConditionalIcon } from '@unifyapps/carbon/utils/conditionalValues';
import type { ComputedButtonAppearance } from '@unifyapps/defs/types/blocks';
import type { ToolbarVariant } from '@unifyapps/table/types';
import { Button } from '@unifyapps/ui/components/Button';
import { clsx } from 'clsx';
import { useMemo } from 'react';
import {
  isUAPlatformToolbar,
  uaPlatformTriggerClassName,
} from '../../../components/FilterMenu/utils/toolbarVariantStyles';

export type FilterButtonProps = {
  onClick?: () => void;
  isSelected?: boolean;
  variant?: 'solid' | 'outline';
  disabled?: boolean;
  style?: React.CSSProperties;
  className?: string;
  appearance?: Omit<ComputedButtonAppearance, 'variant' | 'color'>;
  id: string;
  label: string;
  startDecorator?: React.ReactNode;
  endDecorator?: React.ReactNode;
  toolbarVariant?: ToolbarVariant;
};

export function FilterButton(props: FilterButtonProps) {
  const {
    isSelected,
    variant = 'outline',
    disabled,
    style,
    className,
    appearance,
    id,
    label,
    onClick,
    startDecorator,
    endDecorator,
    toolbarVariant,
  } = props;
  const showHoverStyles = Boolean(isSelected) || variant === 'solid';
  const isUAPlatform = isUAPlatformToolbar(toolbarVariant);

  // todo: discuss with @iamsaumya and fix these by actually implementing a selection pill
  // component - @hatim-s
  const buttonStyle = useMemo(
    () => ({
      '--button-brand-secondary-bg_hover': 'var(--bg-brand-secondary)',
      '--button-brand-secondary-border_hover': 'var(--border-brand-solid)',
      '--button-brand-secondary-fg_hover': 'var(--text-brand-secondary)',
      '--button-neutral-secondary-bg_hover': 'var(--bg-secondary_hover)',
      '--button-neutral-secondary-border': 'var(--border-tertiary)',
      ...style,
    }),
    [style],
  );

  const platformClassName = isUAPlatform
    ? uaPlatformTriggerClassName({ open: false, isApplied: Boolean(isSelected) })
    : undefined;

  return (
    <Button
      aria-pressed={showHoverStyles}
      className={clsx(
        'w-fit !shadow-none',
        className,
        platformClassName,
        !isUAPlatform && {
          '!rounded-full': variant === 'solid',
        },
        !isUAPlatform && { 'border-tertiary hover:border-secondary': !isSelected },
        !isUAPlatform && { 'border-brand-primary hover:border-brand-solid': isSelected },
      )}
      color={isUAPlatform ? 'neutral' : isSelected ? 'brand' : 'neutral'}
      disabled={appearance?.disabled || disabled}
      endDecoratorComponent={getConditionalIcon(appearance?.endDecorator)}
      endDecoratorNode={endDecorator}
      key={id}
      loading={appearance?.loading}
      onClick={onClick}
      size={appearance?.size ?? 'sm'}
      startDecoratorComponent={getConditionalIcon(appearance?.startDecorator)}
      startDecoratorNode={startDecorator}
      style={buttonStyle}
      variant={isUAPlatform ? 'outline' : variant === 'outline' ? 'outline' : 'soft'}
    >
      {label}
    </Button>
  );
}
